/* eslint-disable react-refresh/only-export-components */
import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  AdminAuthAPI,
  type AdminRole,
  type AdminSessionInfo,
  installUnauthorizedHandler,
} from "../api/adminAuth";
import { setAuthenticationStatus } from "../config/EnvironmentConfig";

export interface User {
  id: string;
  email: string;
  name: string;
  isFirstLogin: boolean;
  role: AdminRole;
  roleLabel: string;
}

export interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  // 失敗時はサーバーのメッセージを持つ Error を投げる
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  // 初回ログイン（パスワード変更待ち）のときは currentPassword を空にしてよい
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

const toUser = (s: AdminSessionInfo): User => ({
  id: s.admin_user_id,
  email: s.mail,
  name: s.admin_name,
  isFirstLogin: s.is_first_login,
  role: s.role,
  roleLabel: s.role_label,
});

// 以前の方式で localStorage に残っているトークン（メールアドレスのハッシュ）を消す
const clearLegacyStorage = () => {
  localStorage.removeItem("auth_user");
  localStorage.removeItem("auth_token");
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user;

  useEffect(() => {
    setAuthenticationStatus(isAuthenticated);
  }, [isAuthenticated]);

  // 起動時に、Cookie のセッションが生きているかサーバーに確かめる
  useEffect(() => {
    clearLegacyStorage();
    AdminAuthAPI.session()
      .then((s) => setUser(s ? toUser(s) : null))
      .catch((error) => {
        console.error("ログイン状態の確認に失敗:", error);
        setUser(null);
      })
      .finally(() => setIsLoading(false));
  }, []);

  // 操作中にセッションが切れたら（401）、ログイン画面に戻す
  useEffect(() => installUnauthorizedHandler(() => setUser(null)), []);

  const login = useCallback(async (email: string, password: string) => {
    const s = await AdminAuthAPI.login(email, password);
    setUser(toUser(s));
  }, []);

  const logout = useCallback(async () => {
    try {
      await AdminAuthAPI.logout();
    } finally {
      setUser(null);
    }
  }, []);

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    const s = await AdminAuthAPI.changePassword(currentPassword, newPassword);
    setUser(toUser(s));
  }, []);

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated,
    login,
    logout,
    changePassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
